import { MapPin, Phone, Mail, Clock, ArrowUpRight } from "lucide-react";
import { useBooking } from "@/components/site/BookingProvider";

const LOC_IMG =
  "https://customer-assets.emergentagent.com/job_maui-wellness-new/artifacts/qqvek88w_IMG_7474%20%281%29.jpeg";

export default function Location() {
  const { openBooking } = useBooking();
  return (
    <section
      id="visit"
      data-testid="location-section"
      className="relative bg-bone py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-stretch">
          {/* Image */}
          <div className="lg:col-span-7 mll-reveal">
            <div className="mll-zoom relative aspect-[4/3] lg:aspect-[16/9] w-full overflow-hidden bg-sand">
              <img
                src={LOC_IMG}
                alt="Inside the Maui Longevity Lounge"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>

          {/* Info */}
          <div className="lg:col-span-5 flex flex-col justify-between mll-stagger">
            <div>
              <p className="text-[11px] uppercase tracking-[0.28em] font-semibold text-clay">
                Visit the Lounge
              </p>
              <h2 className="mt-4 font-serif font-medium text-4xl sm:text-5xl tracking-tight text-stone leading-[1.05]">
                Tucked into the green hills of{" "}
                <em className="italic font-normal text-jungle">Haiku.</em>
              </h2>
              <p className="mt-5 text-base text-muted-ink leading-relaxed max-w-md">
                Walk-ins welcome for first consultations. Booking is
                recommended — limited daily slots for HBOT.
              </p>
            </div>

            <div className="mt-10 grid grid-cols-1 gap-px bg-stone/10 border border-stone/10">
              <Row icon={<MapPin className="w-4 h-4" />} label="Address">
                810 Kokomo Rd, Suite 120
                <br />
                Haiku, HI 96708
              </Row>
              <Row icon={<Clock className="w-4 h-4" />} label="Hours">
                Monday — Friday · 8am – 5pm
                <br />
                Closed weekends
              </Row>
              <Row
                icon={<Phone className="w-4 h-4" />}
                label="Call or text"
                link="tel:8085008857"
              >
                808.500.8857
              </Row>
              <Row
                icon={<Mail className="w-4 h-4" />}
                label="Email"
                link="mailto:staff@mauilongevitylounge.com"
              >
                staff@mauilongevitylounge.com
              </Row>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="https://maps.google.com/?q=810+Kokomo+Rd+Suite+120+Haiku+HI+96708"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="directions-link"
                className="inline-flex items-center gap-2 bg-jungle text-bone hover:bg-jungle-dark px-6 h-12 text-[12px] uppercase tracking-[0.22em] font-semibold transition-colors"
              >
                Get directions
                <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
              <button
                type="button"
                onClick={openBooking}
                data-testid="visit-book-button"
                className="inline-flex items-center gap-2 border border-stone/40 text-stone hover:bg-sand px-6 h-12 text-[12px] uppercase tracking-[0.22em] font-semibold transition-colors"
              >
                Book a Session
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Row({ icon, label, children, link }) {
  const content = (
    <div className="flex items-start gap-5 px-6 py-6 bg-bone">
      <span className="mt-1 inline-flex items-center justify-center w-8 h-8 rounded-full border border-stone/15 text-jungle">
        {icon}
      </span>
      <div>
        <p className="text-[10px] uppercase tracking-[0.28em] font-semibold text-muted-ink">
          {label}
        </p>
        <p className="mt-1 text-base text-stone leading-snug">{children}</p>
      </div>
    </div>
  );
  if (link) {
    return (
      <a
        href={link}
        className="block hover:bg-sand/40 transition-colors"
      >
        {content}
      </a>
    );
  }
  return content;
}
